import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { from, merge, Observable, Subscription, take } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { UserService } from '../_services/user.service';
import { CartService } from '../_services/cart.service';
import { PaymentMethodService } from '../_services/payment-method.service';
import { PaymentService } from '../_services/payment.service';
import { PageTitleService } from '../_services/page-title.service';
import { CurrencyConversionService } from '../_services/currency-conversion.service';
import { DeliveryService } from '../_services/delivery.service';

import { User } from '../_models/user';
import { ProductModel } from '../_cart/product-model';
import { CartStateModel } from '../_cart/cart.state';
import { InitTransactionRequestOffer } from '../_models/init-transaction-req-offer';
import { ShippingPricingItem } from '../_models/shipping-pricing-response';
import { PaymentMethods } from '../_constants/payment-methods';
import { AppConstants } from '../_constants/constants';
import { environment } from '../../environments/environment';

declare const FlutterwaveCheckout: any;

@Component({
    selector: 'app-checkout',
    templateUrl: './order-overview.component.html',
    styleUrls: ['./checkout.component.css'],
    standalone: false
})
export class CheckoutComponent implements OnInit, OnDestroy {

    // ── Services ──────────────────────────────────────────────────────────────
    readonly userService              = inject(UserService);
    private readonly fb               = inject(FormBuilder);
    private readonly cartService      = inject(CartService);
    private readonly pageTitleService = inject(PageTitleService);
    private readonly titleService     = inject(Title);
    private readonly router           = inject(Router);
    private readonly activeRoute      = inject(ActivatedRoute);
    private readonly toastr           = inject(ToastrService);
    private readonly paymentService   = inject(PaymentService);
    private readonly paymentMethodService  = inject(PaymentMethodService);
    private readonly translateService      = inject(TranslateService);
    private readonly currencyConverter     = inject(CurrencyConversionService);
    private readonly deliveryService       = inject(DeliveryService);

    // ── State ─────────────────────────────────────────────────────────────────
    readonly title         = 'Check Out';
    readonly titleKey      = 'cart.checkout';
    readonly currency      = environment.currency;
    readonly companyNumber = environment.companyNumber;

    user: User | null = null;
    products!: Observable<ProductModel[]>;
    cart!: CartStateModel;

    orderId         = 0;
    total           = 0;
    shippingTotal   = 0;
    current_fee     = 0;
    totalPlusCharge = 0;
    chosen_method   = '';
    transactionId   = '';
    browserCartId: string | null = null;

    submitting          = false;
    isSuccess           = false;
    paymentSuccess      = false;
    openModal           = false;
    // ✅ FIX: processing flag to prevent duplicate calls
    processing          = false;
    loading             = true;
    initiating          = false;
    usingCompanyMomo    = false;
    prcocessCompanyMomo = false;
    processingId: any;

    readonly methods = [
        { name: 'common.mobile_wallet',    value: 2.5, id: PaymentMethods.FlutterWave },
        { name: 'common.company_money',    value: 0,   id: PaymentMethods.CompanyMobileMoney }
    ];

    // exposed for the HTML payment method dropdown
    paymentMethods = this.methods;

    chosen_method_fee = 0;
    paymentMethodForm!: FormGroup;

    // ── Forms ─────────────────────────────────────────────────────────────────
    form!: FormGroup;
    companyPaymentDetailsForm!: FormGroup;

    // ── Subscriptions ─────────────────────────────────────────────────────────
    private readonly subs: Subscription[] = [];

    // ── Lifecycle ─────────────────────────────────────────────────────────────
    ngOnInit(): void {
        this.pageTitleService.setPageTitle(this.title);
        this.titleService.setTitle(this.title);
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? 'null');
        this.buildForms();
        this.initProducts();
        this.setTotalAndCurrency();
        window.scrollTo(0, 0);
    }

    ngOnDestroy(): void {
        clearInterval(this.processingId);
        this.subs.forEach(s => s.unsubscribe());
    }

    // ── Form builders ─────────────────────────────────────────────────────────
    private buildForms(): void {
        // ✅ FIX: form controls now match ALL formControlName values used in the HTML
        this.form = this.fb.group({
            shippingAddressId: new FormControl<number | null>(null, Validators.required),
            paymentMethodId:   new FormControl<number | null>(null, Validators.required),
            phoneNumber:       new FormControl<string | null>(null, [
                Validators.required,
                Validators.minLength(9)
            ])
        });

        this.paymentMethodForm = new FormGroup({
            fee: new FormControl(this.methods[0].name, Validators.required)
        });

        this.paymentMethodForm.get('fee')!.valueChanges.subscribe(value => {
            this.chosen_method   = value;
            this.current_fee     = this.getFee();
            this.totalPlusCharge = this.total + this.current_fee;
        });

        this.companyPaymentDetailsForm = new FormGroup({
            moMoTxRef:   new FormControl('', [Validators.required]),
            phoneNumber: new FormControl('', [
                Validators.required,
                Validators.minLength(9),
                Validators.maxLength(9)
            ])
        });
    }

    private initProducts(): void {
        this.products = this.cartService.getProducts();

        this.subs.push(
            this.products.subscribe(values => {
                if (values.length === 0 && !this.isSuccess) {
                    this.translateService.get('cart.is_empty_message').subscribe(msg => {
                        this.toastr.warning(msg);
                        this.router.navigateByUrl('shopping-cart');
                    });
                } else {
                    const selectedItems = values.filter(p => p.selected);
                    if (selectedItems.length === 0 && !this.isSuccess) {
                        this.translateService.get('cart.select_items_to_checkout').subscribe(msg => {
                            this.toastr.warning(msg);
                            this.router.navigateByUrl('shopping-cart');
                        });
                    }
                }
            }),
            this.cartService.getOrderId().subscribe(value => {
                if (value !== 0) { this.orderId = value; }
            }),
            this.cartService.getClientCartId().subscribe(value => {
                if (value) { this.browserCartId = value; }
            })
        );

        this.chosen_method = this.methods[0].name;
        this.paymentMethodForm.get('fee')!.setValue(this.chosen_method);
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    get f() { return this.form.controls; }

    getSelectedProducts(products: ProductModel[]): ProductModel[] {
        return products.filter(p => p.selected);
    }

    getImageSrc(imagePath: string): string {
        return imagePath && imagePath.includes('media')
            ? imagePath.includes('?') ? imagePath : `${imagePath}?func=crop&width=75&height=75`
            : '/assets/images/default.jpg';
    }

    getShippingForProduct(offerId: number): ShippingPricingItem | undefined {
        return this.deliveryService.getSelection(offerId);
    }

    getShippingLabel(pricing: ShippingPricingItem): string {
        if (!pricing) return '';
        return pricing.shippingOption?.logisticAgent
            || pricing.shippingOption?.shippingType?.description
            || 'Standard';
    }

    getFee(): number {
        const method = this.methods.find(m => m.name === this.chosen_method);
        return method ? (method.value * this.total) / 100 : 0;
    }

    getItem(m: ProductModel): InitTransactionRequestOffer {
        const isLocalPickup = this.deliveryService.isLocalPickupSelected(m.id);
        const selectedPricing = isLocalPickup ? null : this.deliveryService.getSelection(m.id);

        const item: InitTransactionRequestOffer = {
            offerId: m.id,
            qty: m.qty,
            shippingPricingId: selectedPricing?.id ?? null
        };

        if (isLocalPickup) {
            item.localPickup = true;
        }

        return item;
    }

    getExtraProps(currentUsr: User, data: CartStateModel): object {
        return {
            additionalProp1: { userId:     currentUsr.id },
            additionalProp2: { userCode:   currentUsr.code },
            additionalProp3: { clientCode: data.cartId }
        };
    }

    // ── Step navigation (used by HTML next/previous buttons) ──────────────────
    gotoStep(current: string, next: string): void {
        const currentEl = document.getElementById(`${current}-content`);
        const nextEl    = document.getElementById(`${next}-content`);
        const nextLi    = document.getElementById(next);

        if (currentEl) { currentEl.style.display = 'none'; }
        if (nextEl)    { nextEl.style.display = 'block'; }
        if (nextLi)    { nextLi.classList.add('active'); }
    }

    // ── Modal helpers ─────────────────────────────────────────────────────────
    selectPaymentMethod(): void { this.openModal = true; }

    closeModal(): void {
        this.openModal = false;
        if (this.isSuccess) {
            this.router.navigateByUrl(`user/${this.user!.id}/orders`);
        }
    }

    closeCompanyMobileMoney(): void { this.usingCompanyMomo = false; }

    // ── Payment flow ──────────────────────────────────────────────────────────
    place_your_order(): void {
        // ✅ FIX: guard against duplicate calls
        if (this.processing) {
            console.warn('Payment already in progress — duplicate call ignored.');
            return;
        }

        if (this.isSuccess) { return; }

        const currentUser = this.userService.getCurrntUser();
        if (!currentUser) {
            this.translateService.get('common.message_for_signin').subscribe(msg => {
                this.toastr.error(msg);
                this.router.navigate(['/', 'signin'], {
                    relativeTo: this.activeRoute,
                    queryParams: { returnUrl: '/checkout' }
                });
            });
            return;
        }

        // ✅ FIX: set processing = true before starting
        this.processing = true;
        this.user = currentUser;
        const paymentMethodObj = this.methods.find(m => m.name === this.chosen_method)!;

        if (paymentMethodObj.id === PaymentMethods.CompanyMobileMoney) {
            this.usingCompanyMomo = true;
            this.openModal = false;
            this.processing = false;
            return;
        }

        const sub = this.cartService.getCart()
            .pipe(
                switchMap(data => {
                    this.cart = data;
                    const selectedProducts = data.products.filter(p => p.selected);
                    return this.paymentMethodService.getTransactionRef({
                        paymentType:              'ORDER',
                        referenceId:              data.cartId,
                        amount:                   this.total,
                        feeAmount:                this.getFee(),
                        paymentMethod:            paymentMethodObj.id,
                        phoneNumber:              this.user!.phone,
                        mobileMoneyTransactionId: '',
                        subscriptionPeriod:       '',
                        items:                    selectedProducts.map(m => this.getItem(m))
                    }, this.user!.firstName + ' ' + this.user!.lastName);
                })
            )
            .subscribe({
                next: transaction => {
                    this.transactionId = transaction.data?.transactionRef || transaction.txRef || transaction.transactionRef;

                    if (paymentMethodObj.id === PaymentMethods.FlutterWave) {
                        this.openModal = false;

                        const isMobileWallet = this.chosen_method === 'common.mobile_wallet';

                        if (typeof FlutterwaveCheckout === 'undefined') {
                            this.processing = false;
                            this.toastr.error('Payment system not loaded. Please refresh and try again.');
                            return;
                        }

                        FlutterwaveCheckout({
                            public_key:      environment.flutterwaveKey,
                            tx_ref:          this.transactionId,
                            amount:          this.totalPlusCharge,
                            currency:        this.currency,
                            payment_options: isMobileWallet ? 'mobilemoneyfranco,ussd' : 'card',
                            customer: {
                                name:         this.user!.firstName + ' ' + this.user!.lastName,
                                email:        this.user!.email,
                                phone_number: this.user!.phone
                            },
                            customizations: {
                                title:       'eFarm.cm',
                                description: 'eFarm.CM Checkout',
                                logo:        'https://efarm.cm/assets/images/logo.png'
                            },
                            callback: (response: any) => {
                                this.paymentSuccess = true;
                                this.paymentMethodService.confirmOrderPayment({
                                    status: response.status,
                                    transaction_id: response.transaction_id,
                                    tx_ref: response.tx_ref,
                                    flw_ref: response.flw_ref,
                                    currency: response.currency,
                                    amount: response.amount,
                                    customer: {
                                        name: this.user!.firstName + ' ' + this.user!.lastName,
                                        email: this.user!.email,
                                        phone_number: this.user!.phone
                                    }
                                }).subscribe({
                                    next: (res: any) => {
                                        this.toastr.success(res.message || 'Payment confirmed successfully');
                                        localStorage.removeItem(AppConstants.USER_AGENT_TOKEN);
                                        this.showOrders();
                                    },
                                    error: () => {
                                        this.processing = false;
                                        this.toastr.error('Payment confirmation failed. Please contact support.');
                                    }
                                });
                            },
                            onclose: () => {
                                this.processing = false;
                                if (this.paymentSuccess) {
                                    return;
                                }
                                this.paymentMethodService.confirmOrderPayment({
                                    status: 'CANCELLED',
                                    tx_ref: this.transactionId
                                }).subscribe();
                                this.toastr.error('Payment was cancelled. Please try again.');
                            }
                        });
                    }
                },
                error: err => {
                    // ✅ FIX: always reset processing on error
                    this.processing = false;
                    // ✅ FIX: was if(error = '...') — assignment not comparison
                    const msg: string = err?.message ?? '';
                    if (msg === 'Reset your cart. Some products are no longer exist in our system') {
                        this.toastr.error(msg);
                    } else {
                        this.toastr.error('An error occurred during payment. Please try again.');
                    }
                },
            });

        this.subs.push(sub);
    }

    showOrders(): void {
        this.isSuccess  = true;
        this.processing = false;
        // Stop cart watchers so clearing the cart can't trigger the empty-cart toast
        this.subs.forEach(s => s.unsubscribe());
        this.subs.length = 0;
        this.cartService.removeAgentCartId();
        this.deliveryService.clearSelections();
        localStorage.removeItem('cart');
        this.cartService.clearCart().subscribe({
            next: () => window.location.href = '/user/' + this.user!.id + '/orders'
        });
    }

    processCompanyMobileMoney(): void {
        this.prcocessCompanyMomo = true;
        if (!this.companyPaymentDetailsForm.valid || this.processing) { return; }

        this.processing = true;
        const formValue = this.companyPaymentDetailsForm.value;
        const fullName = this.user!.firstName + ' ' + this.user!.lastName;

        // ✅ FIX: the backend confirms the payment directly in init-payment for
        // COMPANY_MOBILE_MONEY (phone number + MoMo tx ref are part of the payload),
        // so no second register_payment_details call is needed.
        const sub = this.cartService.getCart()
            .pipe(
                switchMap(data => {
                    this.cart = data;
                    const selectedProducts = data.products.filter(p => p.selected);
                    return this.paymentMethodService.getTransactionRef({
                        paymentType:              'ORDER',
                        referenceId:              data.cartId,
                        amount:                   this.total,
                        feeAmount:                this.getFee(),
                        paymentMethod:            PaymentMethods.CompanyMobileMoney,
                        phoneNumber:              formValue.phoneNumber,
                        mobileMoneyTransactionId: formValue.moMoTxRef,
                        subscriptionPeriod:       '',
                        items:                    selectedProducts.map(m => this.getItem(m))
                    }, fullName);
                })
            )
            .subscribe({
                next: (response: any) => {
                    this.transactionId = response?.data?.transactionRef
                        || response?.transactionRef
                        || response?.tx_ref;

                    if (response?.data?.status === 'CONFIRMED') {
                        this.toastr.success(response.message || 'Payment confirmed successfully');
                        this.usingCompanyMomo = false;
                        this.prcocessCompanyMomo = false;
                        this.showOrders();
                    } else {
                        this.toastr.error(response?.message || 'Payment failed. Please try again.');
                        this.processing = false;
                        this.prcocessCompanyMomo = false;
                    }
                },
                error: () => {
                    this.processing = false;
                    this.prcocessCompanyMomo = false;
                    this.toastr.error('Payment processing failed. Please try again.');
                }
            });

        this.subs.push(sub);
    }

    private setTotalAndCurrency(): void {
        this.subs.push(
            this.products.pipe(
                take(1),
                switchMap(products => from(products.filter(p => p.selected))),
                mergeMap(product => {
                    const productCost$ = this.currencyConverter.convert(
                        product.qty * product.price,
                        product.currency
                    );
                    const isLocalPickup = this.deliveryService.isLocalPickupSelected(product.id);
                    if (!isLocalPickup) {
                        const selectedPricing = this.deliveryService.getSelection(product.id);
                        if (selectedPricing) {
                            const shippingCost$ = this.currencyConverter.convert(
                                selectedPricing.price * product.qty,
                                this.currency
                            );
                            return merge(productCost$, shippingCost$);
                        }
                    }
                    return productCost$;
                }, 1)
            ).subscribe({
                next: (amount: number) => { this.total += amount; },
                complete: () => {
                    this.current_fee = this.getFee();
                    this.totalPlusCharge = this.total + this.current_fee;
                }
            })
        );

        this.subs.push(
            this.products.pipe(
                take(1),
                switchMap(products => from(products.filter(p => p.selected))),
                mergeMap(product => {
                    const isLocalPickup = this.deliveryService.isLocalPickupSelected(product.id);
                    if (isLocalPickup) {
                        return from([0]);
                    }
                    const selectedPricing = this.deliveryService.getSelection(product.id);
                    if (selectedPricing) {
                        return this.currencyConverter.convert(
                            selectedPricing.price * product.qty,
                            this.currency
                        );
                    }
                    return from([0]);
                }, 1)
            ).subscribe(amount => { this.shippingTotal += amount; })
        );
    }
}
